import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  AxonCheckView,
  AxonExpectationView,
  AxonSubsystemView,
  OverviewCategory,
  OverviewCheckRow,
  PlatformHealthAxonOverviewResponse,
  PlatformHealthOverviewResponse,
} from '@/pages/platform-health/types';
import { Stoplight } from '@/pages/platform-health/Stoplight';
import {
  resolveCheck,
  resolveExpectation,
  resolveSubsystem,
  resolveSystem,
} from '@/pages/platform-health/stoplightResolver';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  HelpCircle,
  RefreshCw,
  ShieldAlert,
  XCircle,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const REFRESH_INTERVAL_MS = 60 * 1000;

function relative(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return 'unknown';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

const TONE = {
  healthy:
    'border-green-300 bg-green-50 text-green-900 dark:border-green-800 dark:bg-green-950/40 dark:text-green-100',
  degraded:
    'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100',
  unhealthy:
    'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950/40 dark:text-red-100',
  blocked:
    'border-red-400 bg-red-100 text-red-950 dark:border-red-700 dark:bg-red-950/70 dark:text-red-50',
  unknown:
    'border-gray-300 bg-gray-50 text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-gray-100',
} as const;

function tone(state: string): keyof typeof TONE {
  return state === 'healthy' || state === 'degraded' || state === 'unhealthy' || state === 'blocked'
    ? state
    : 'unknown';
}

export function AxonObservationPanel({
  core,
  watchdog,
}: Pick<PlatformHealthOverviewResponse, 'core' | 'watchdog'>) {
  const healthy =
    core.available && watchdog.available && watchdog.status === 'pass' && !watchdog.stale;
  const panelTone = healthy
    ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/40'
    : 'border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950/40';
  const Icon = healthy ? CheckCircle2 : AlertTriangle;
  const title = healthy ? 'AXON independently verified' : 'AXON degraded or unverified';

  return (
    <section
      data-testid="axon-observation-panel"
      data-status={healthy ? 'healthy' : 'degraded'}
      className={cn('rounded-lg border p-4', panelTone)}
    >
      <div className="flex items-start gap-3">
        <Icon
          className={cn('mt-0.5 h-5 w-5 shrink-0', healthy ? 'text-green-600' : 'text-red-600')}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
            {watchdog.runUrl ? (
              <a
                href={watchdog.runUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                Open watchdog run
              </a>
            ) : null}
          </div>
          <dl className="mt-3 grid gap-3 text-xs sm:grid-cols-3">
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Canonical AXON query</dt>
              <dd className="mt-0.5 font-medium text-gray-900 dark:text-white">
                {core.available ? 'Available' : 'Unavailable'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Independent watchdog</dt>
              <dd className="mt-0.5 font-medium uppercase text-gray-900 dark:text-white">
                {watchdog.available ? watchdog.status : 'unavailable'}
                {watchdog.stale ? ' · stale' : ''}
              </dd>
              {watchdog.observedAt ? (
                <dd className="text-gray-500 dark:text-gray-400">
                  Observed {relative(watchdog.observedAt)}
                  {watchdog.deployedTarget ? ` · ${watchdog.deployedTarget} observer` : ''}
                </dd>
              ) : null}
            </div>
            <div>
              <dt className="text-gray-500 dark:text-gray-400">Last successful E2E canary</dt>
              <dd className="mt-0.5 font-medium text-gray-900 dark:text-white">
                {watchdog.lastSuccessfulCanaryAt
                  ? relative(watchdog.lastSuccessfulCanaryAt)
                  : 'Never observed'}
              </dd>
            </div>
          </dl>
          {!core.available ? (
            <p className="mt-3 text-xs text-red-800 dark:text-red-200">
              The primary AXON query is unavailable. The watchdog result above comes from its
              independent evidence path; this state is not an all-clear.
            </p>
          ) : null}
          {!watchdog.available || watchdog.reason ? (
            <p className="mt-3 text-xs text-red-800 dark:text-red-200">
              {watchdog.reason === 'watchdog_never_observed'
                ? 'The independent watchdog has never reported.'
                : 'The independent watchdog evidence cannot be read.'}{' '}
              This state is not an all-clear.
            </p>
          ) : null}
          {watchdog.findings.length > 0 ? (
            <ul className="mt-3 space-y-1 text-xs text-red-800 dark:text-red-200">
              {watchdog.findings.map((finding) => (
                <li key={`${finding.stage}:${finding.code}`}>
                  <span className="font-semibold">{finding.stage}</span>/{finding.code}:{' '}
                  {finding.summary}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  warning = false,
}: {
  label: string;
  value: number;
  warning?: boolean;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      <dd
        className={cn(
          'mt-1 text-2xl font-semibold tabular-nums text-gray-950 dark:text-white',
          warning && value > 0 && 'text-amber-700 dark:text-amber-300'
        )}
      >
        {value}
      </dd>
    </div>
  );
}

function Axis({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-xs font-medium text-gray-900 dark:text-white">
        {(value ?? 'unknown').replace(/_/g, ' ')}
      </dd>
    </div>
  );
}

function CanonicalState({ state }: { state: string }) {
  return (
    <span className="text-[10px] text-gray-500 dark:text-gray-400">
      Canonical: {state.replace(/_/g, ' ')}
    </span>
  );
}

function timestamp(value: string | null): string {
  return value ? new Date(value).toLocaleString() : 'Not recorded';
}

function triggerLabel(trigger: AxonExpectationView['trigger']): string {
  switch (trigger.kind) {
    case 'schedule':
      return `Schedule every ${trigger.cadenceSeconds}s (grace ${trigger.graceSeconds}s)`;
    case 'event':
      return `Event: ${trigger.eventType}${trigger.maxStartDelaySeconds ? ` (start ≤ ${trigger.maxStartDelaySeconds}s)` : ''}`;
    case 'release':
      return `Release: ${trigger.stage} (start ≤ ${trigger.maxStartDelaySeconds}s)`;
    case 'manual':
      return trigger.cadenceSeconds
        ? `Manual cadence ${trigger.cadenceSeconds}s`
        : 'Manual monitor';
  }
}

function nonGreenCount(values: Array<{ value: string }>): number {
  return values.filter((item) => item.value !== 'green' && item.value !== 'neutral').length;
}

function ExpectationCard({
  check,
  expectation,
}: {
  check: AxonCheckView;
  expectation: AxonExpectationView;
}) {
  const presentation = resolveExpectation(
    expectation,
    check.actionableFindingCount > 0 || check.blockingFindingCount > 0
  );
  return (
    <section className="min-w-0 rounded border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <code className="break-all text-[11px] text-gray-500 dark:text-gray-400">
          {expectation.expectationId}
        </code>
        <div className="flex flex-wrap items-center gap-2">
          <Stoplight value={presentation.value} reason={presentation.reason} />
          <CanonicalState state={expectation.healthState} />
          <span className="text-xs font-semibold uppercase text-gray-900 dark:text-white">
            {expectation.conclusion.replace(/_/g, ' ')}
          </span>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Axis label="Outcome" value={expectation.status.outcome} />
        <Axis label="Applicability" value={expectation.status.applicability} />
        <Axis label="Freshness" value={expectation.status.freshness} />
        <Axis label="Completeness" value={expectation.status.completeness} />
        <Axis label="Policy" value={expectation.status.policy} />
      </dl>
      <dl className="mt-3 grid gap-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
        <Axis label="Environment" value={expectation.scope.environment} />
        <Axis label="Target" value={expectation.scope.targetId} />
        <Axis label="Release" value={expectation.scope.releaseId} />
        <Axis label="Test definition" value={expectation.scope.testDefinitionId} />
      </dl>
      <div className="mt-3 space-y-1 text-xs text-gray-600 dark:text-gray-300">
        <p>Trigger / cadence: {triggerLabel(expectation.trigger)}</p>
        <p>
          Expected: {timestamp(expectation.expectedAt)} · Due: {timestamp(expectation.dueAt)}
        </p>
        <p>
          Observed: {timestamp(expectation.observedAt)} · Attested:{' '}
          {timestamp(expectation.currentAttestation?.attestedAt ?? null)}
        </p>
        {expectation.currentAttestation ? (
          <p>
            Current attestation: <strong>{expectation.currentAttestation.summary}</strong>
            {expectation.currentAttestation.validUntil
              ? ` (valid until ${timestamp(expectation.currentAttestation.validUntil)})`
              : ''}
          </p>
        ) : (
          <p className="font-medium text-red-700 dark:text-red-300">
            Current attestation is missing.
          </p>
        )}
      </div>
      <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">{expectation.explanation}</p>
      <a
        href={`/admin/platform-health/test-runs?subsystem=${encodeURIComponent(
          check.checkId.split('.')[0]
        )}&run=${encodeURIComponent(
          expectation.latestRunId
            ? `run:${expectation.latestRunId}`
            : `expectation:${expectation.expectationId}`
        )}`}
        className="mt-2 inline-flex max-w-full flex-wrap items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
      >
        {expectation.latestRunId ? 'Open run history' : 'Open expected run'}
        <ExternalLink className="h-3 w-3 shrink-0" aria-hidden="true" />
      </a>
      {expectation.evidence.length > 0 ? (
        <ul className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
          {expectation.evidence.map((evidence) => (
            <li key={evidence.evidenceReferenceId} className="break-all">
              {evidence.uri && !evidence.missing ? (
                <a
                  href={evidence.uri}
                  className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  {evidence.kind} evidence
                </a>
              ) : (
                <span className="font-medium text-red-700 dark:text-red-300">
                  Missing {evidence.kind} evidence
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-xs font-medium text-red-700 dark:text-red-300">Missing evidence.</p>
      )}
      <p className="mt-2 break-all text-[10px] text-gray-500 dark:text-gray-400">
        Inputs: {expectation.inputRecordIds.join(', ') || 'none'}
      </p>
    </section>
  );
}

function AxonCheck({ check }: { check: AxonCheckView }) {
  const presentation = resolveCheck(check);
  const nonGreenExpectations = nonGreenCount(
    check.expectations.map((expectation) =>
      resolveExpectation(
        expectation,
        check.actionableFindingCount > 0 || check.blockingFindingCount > 0
      )
    )
  );
  return (
    <details
      data-testid="axon-overview-check"
      className="group border-t border-gray-200 first:border-t-0 dark:border-zinc-700"
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500">
        <ChevronRight
          className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-950 dark:text-white">
              {check.displayName}
            </span>
            <span
              className={cn(
                'rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                TONE[tone(check.state)]
              )}
            >
              {check.state.replace(/_/g, ' ')}
            </span>
            <Stoplight value={presentation.value} reason={presentation.reason} />
            <CanonicalState state={check.state} />
            {check.coverage !== 'covered' ? (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                {check.coverage.replace(/_/g, ' ')}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{check.explanation}</p>
          {nonGreenExpectations > 0 ? (
            <p className="mt-1 text-xs font-medium text-red-700 dark:text-red-300">
              {nonGreenExpectations} non-green expectation{nonGreenExpectations === 1 ? '' : 's'}
            </p>
          ) : null}
        </div>
      </summary>
      <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900/40">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="text-gray-600 dark:text-gray-300">
            Owner: <strong>{check.owner.displayName}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-300">
            {check.activeFindingCount} active / {check.blockingFindingCount} blocking findings
            {check.actionableFindingCount > 0
              ? ` (${check.actionableFindingCount} actionable)`
              : ''}
          </span>
          <a
            href={check.workbenchLink}
            className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            Open owning workbench <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
        {check.expectations.length === 0 ? (
          <p className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            No active expectation exists. AXON has a registered denominator but no evidence window
            to assess.
          </p>
        ) : (
          <div className="mt-4 space-y-3">
            {check.expectations.map((expectation) => (
              <ExpectationCard
                key={expectation.expectationId}
                check={check}
                expectation={expectation}
              />
            ))}
          </div>
        )}
      </div>
    </details>
  );
}

function SubsystemCard({ subsystem }: { subsystem: AxonSubsystemView }) {
  const presentation = resolveSubsystem(subsystem);
  const nonGreenChecks = nonGreenCount(subsystem.checks.map(resolveCheck));
  return (
    <section
      data-testid="axon-subsystem-card"
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-zinc-800"
    >
      <header className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-gray-950 dark:text-white">
                {subsystem.displayName}
              </h2>
              <span
                className={cn(
                  'rounded border px-2 py-0.5 text-[10px] font-semibold uppercase',
                  TONE[tone(subsystem.state)]
                )}
              >
                {subsystem.state}
              </span>
              <Stoplight value={presentation.value} reason={presentation.reason} />
              <CanonicalState state={subsystem.state} />
            </div>
            <p className="mt-1 max-w-3xl text-xs text-gray-600 dark:text-gray-300">
              {subsystem.description}
            </p>
          </div>
          <a
            href={subsystem.workbenchLink}
            className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            Workbench <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500 dark:text-gray-400">
          <span>{subsystem.checks.length} registered checks</span>
          {nonGreenChecks > 0 ? <span>{nonGreenChecks} non-green checks</span> : null}
          <span>{subsystem.activeFindingCount} active findings</span>
          <span>{subsystem.actionableFindingCount} actionable findings</span>
          <span>{subsystem.blockingFindingCount} blocking findings</span>
          <span>{subsystem.blindSpotCount} blind spots</span>
          <span>Self-health: {subsystem.selfHealth?.state.replace(/_/g, ' ') ?? 'not proved'}</span>
          <span>Owner: {subsystem.owner.displayName}</span>
        </div>
        <p className="mt-3 text-xs text-gray-700 dark:text-gray-200">{subsystem.explanation}</p>
      </header>
      {subsystem.checks.length > 0 ? (
        <div className="border-t border-gray-200 dark:border-zinc-700">
          {subsystem.checks.map((check) => (
            <AxonCheck key={check.checkId} check={check} />
          ))}
        </div>
      ) : (
        <div className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
          This subsystem is registered but has no checks. It cannot be healthy by omission.
        </div>
      )}
    </section>
  );
}

export function AxonOverviewContent({ payload }: { payload: PlatformHealthAxonOverviewResponse }) {
  const systemPresentation = resolveSystem(payload);
  const systemTone =
    systemPresentation.value === 'green'
      ? 'healthy'
      : systemPresentation.value === 'amber'
        ? 'degraded'
        : systemPresentation.value === 'red'
          ? 'unhealthy'
          : 'unknown';
  return (
    <div className="space-y-4">
      {!payload.available ? (
        <div
          data-testid="axon-overview-unavailable"
          className={cn('rounded-lg border p-4 text-sm', TONE.unhealthy)}
        >
          <div className="flex items-start gap-2">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <div>
              <p className="font-semibold">AXON cannot attest platform health</p>
              <p className="mt-1">
                The canonical snapshot or matching registry is unavailable. This is not an
                all-clear.
              </p>
            </div>
          </div>
        </div>
      ) : null}
      {payload.partial ? (
        <div
          data-testid="axon-overview-partial"
          className={cn('rounded-lg border p-4 text-sm', TONE.unhealthy)}
        >
          <p className="font-semibold">Partial evidence — do not interpret this page as complete</p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
            {payload.gaps.map((gap, index) => (
              <li key={`${gap.source}:${gap.code}:${index}`}>{gap.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <section
        className={cn('rounded-xl border p-5', TONE[systemTone])}
        data-testid="axon-system-attestation"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider">System attestation</p>
            <div className="mt-1 flex flex-wrap items-center gap-3">
              <Stoplight
                value={systemPresentation.value}
                reason={systemPresentation.reason}
                className="text-2xl"
              />
              <h2 className="text-2xl font-semibold capitalize">
                {payload.available ? (payload.system?.state ?? 'unknown') : 'unavailable'}
              </h2>
              <CanonicalState
                state={payload.available ? (payload.system?.state ?? 'unknown') : 'unavailable'}
              />
            </div>
            <p className="mt-2 max-w-3xl text-sm">
              {payload.system?.explanation ?? 'No canonical system attestation can be read.'}
            </p>
          </div>
          <div className="text-right text-xs">
            <p>{payload.environment}</p>
            <p className="mt-1 opacity-75">
              {payload.evaluatedAt
                ? `Evaluated ${relative(payload.evaluatedAt)}`
                : 'Never evaluated'}
            </p>
          </div>
        </div>
      </section>
      <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <Metric label="Registered" value={payload.coverage.registeredChecks} />
        <Metric label="Covered" value={payload.coverage.coveredChecks} />
        <Metric
          label="Missing expectations"
          value={payload.coverage.missingExpectationChecks}
          warning
        />
        <Metric label="Never seen" value={payload.coverage.neverSeenExpectations} warning />
        <Metric
          label="Late / stale"
          value={payload.coverage.overdueExpectations + payload.coverage.staleExpectations}
          warning
        />
        <Metric label="Active findings" value={payload.findings.active} warning />
        <Metric label="Critical" value={payload.findings.critical} warning />
      </dl>
      <AxonObservationPanel
        core={{
          available: payload.available,
          reason: payload.available ? null : 'primary_query_unavailable',
        }}
        watchdog={payload.watchdog}
      />
      {payload.parity.available ? (
        <section className="rounded-lg border border-gray-200 bg-white p-4 text-xs dark:border-zinc-700 dark:bg-zinc-800">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-gray-950 dark:text-white">Compatibility parity</h2>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium uppercase text-gray-700 dark:bg-zinc-700 dark:text-gray-200">
              {payload.parity.comparable
                ? payload.parity.semanticMatch
                  ? 'match'
                  : 'mismatch'
                : 'not comparable'}
            </span>
          </div>
          {payload.parity.limitations.map((limitation) => (
            <p key={limitation} className="mt-2 text-gray-600 dark:text-gray-300">
              {limitation}
            </p>
          ))}
        </section>
      ) : null}
      <div className="space-y-4">
        {payload.subsystems.map((subsystem) => (
          <SubsystemCard key={subsystem.subsystemId} subsystem={subsystem} />
        ))}
      </div>
      {payload.blindSpots.length > 0 ? (
        <section className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-amber-950 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Blind spots and evidence debt
          </h2>
          <ul className="mt-3 space-y-2 text-xs text-amber-900 dark:text-amber-100">
            {payload.blindSpots.map((spot) => (
              <li
                key={spot.id}
                className="flex flex-wrap items-start justify-between gap-2 rounded border border-amber-200 bg-white/60 p-2 dark:border-amber-900 dark:bg-zinc-900/40"
              >
                <span>
                  <strong>{spot.code.replace(/_/g, ' ')}</strong> · {spot.summary}
                </span>
                {spot.workbenchLink ? (
                  <a href={spot.workbenchLink} className="font-medium underline">
                    Workbench
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

function legacyRowState(row: OverviewCheckRow): 'fail' | 'warn' | 'pass' | 'unknown' {
  const value = row.verdict.toUpperCase();
  if (['FAIL', 'ERROR', 'CRITICAL'].includes(value)) return 'fail';
  if (row.stale || ['WARN', 'WARNING'].includes(value)) return 'warn';
  if (['PASS', 'OK'].includes(value)) return 'pass';
  return 'unknown';
}

const LEGACY_ICON = {
  fail: XCircle,
  warn: AlertTriangle,
  pass: CheckCircle2,
  unknown: HelpCircle,
} as const;

function CompatibilityCategory({ group }: { group: OverviewCategory }) {
  return (
    <section className="overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
      <header className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5 dark:border-zinc-700">
        <h2 className="text-sm font-semibold capitalize text-gray-900 dark:text-white">
          {group.category.replace(/_/g, ' ')}
        </h2>
        <span className="text-xs font-semibold uppercase">{group.worst}</span>
      </header>
      <ul>
        {group.rows.map((row) => {
          const state = legacyRowState(row);
          const Icon = LEGACY_ICON[state];
          return (
            <li
              key={`${row.checkName}:${row.env}`}
              className="flex items-start gap-3 border-b border-gray-200 px-4 py-3 last:border-b-0 dark:border-zinc-700"
            >
              <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-950 dark:text-white">
                  {row.displayName}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {row.summary || 'No summary'} · {row.env}
                </p>
              </div>
              <div className="text-right text-xs text-gray-500">
                <p>{row.verdict}</p>
                <p>{relative(row.ts)}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export function PlatformHealthOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const compatibility = searchParams.get('readModel') === 'compatibility';
  const axonQuery = useQuery({
    queryKey: ['platform-health', 'axon', 'overview'],
    queryFn: () => api.getPlatformHealthAxonOverview(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: !compatibility,
  });
  const compatibilityQuery = useQuery({
    queryKey: ['platform-health', 'compatibility', 'overview'],
    queryFn: () => api.getPlatformHealthOverview(),
    refetchInterval: REFRESH_INTERVAL_MS,
    enabled: compatibility,
  });
  const query = compatibility ? compatibilityQuery : axonQuery;
  const payload = compatibility ? compatibilityQuery.data?.data : axonQuery.data?.data;

  function switchModel() {
    const next = new URLSearchParams(searchParams);
    if (compatibility) next.delete('readModel');
    else next.set('readModel', 'compatibility');
    setSearchParams(next);
  }

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Platform Health"
        subtitle="Canonical subsystem coverage, evidence freshness, findings, and self-health"
      />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {payload?.generatedAt
            ? `Updated ${relative(payload.generatedAt)}`
            : 'Loading current state…'}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={switchModel}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            {compatibility ? 'Return to AXON' : 'Compatibility view'}
          </button>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', query.isFetching && 'animate-spin')}
              aria-hidden="true"
            />{' '}
            Refresh
          </button>
        </div>
      </div>
      {compatibility ? (
        <div
          data-testid="compatibility-read-model"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Compatibility read model.</strong> This legacy sink view is retained for rollback
          and parity inspection; it is not the canonical AXON attestation.
        </div>
      ) : null}
      {query.isError ? (
        <div
          data-testid="platform-health-overview-error"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          Couldn&apos;t load platform health: {(query.error as Error)?.message || 'unknown error'}
        </div>
      ) : null}
      {query.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-400">
          Loading platform health…
        </div>
      ) : null}
      {!compatibility && axonQuery.data?.data ? (
        <AxonOverviewContent payload={axonQuery.data.data} />
      ) : null}
      {compatibility && compatibilityQuery.data?.data ? (
        <div className="space-y-4">
          <AxonObservationPanel
            core={compatibilityQuery.data.data.core}
            watchdog={compatibilityQuery.data.data.watchdog}
          />
          {compatibilityQuery.data.data.categories.length === 0 ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-8 text-center text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
              No compatibility check has reported. This is not an all-clear.
            </div>
          ) : (
            compatibilityQuery.data.data.categories.map((group) => (
              <CompatibilityCategory key={group.category} group={group} />
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
